import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Popconfirm, Space, Spin, Tag, Typography, message } from 'antd';
import { useApi } from '../../hooks/use_api';
import { useNodeApps } from '../../hooks/use_node_apps';
import PageApp from './PageApp';
import './AppPage.css';

const { Title, Text } = Typography;

// One installed app instance (/app/:instanceId). The header is the same for
// every app: where it is installed — its entity hosts, and the node itself
// when a superadmin installed it there — and below it, the app's own view.
// Apps without a web view yet show their manifest.
const VIEWS = { page: PageApp };

function AppPage() {
    const { instanceId } = useParams();
    const api = useApi();
    const { refresh: refreshNodeApps } = useNodeApps();
    const [instance, setInstance] = useState(null);
    const [error, setError] = useState(null);
    const [hosts, setHosts] = useState({});

    const load = useCallback(() => api.get(`/api/apps/${instanceId}`)
        .then(inst => {
            setInstance(inst);
            return Promise.all(inst.host_ids.map(id => api.get(`/api/entities/${id}`).catch(() => null)));
        })
        .then(rows => setHosts(Object.fromEntries((rows || []).filter(Boolean).map(e => [e.id, e]))))
        .catch(err => setError(err.status === 403 ? 'You cannot use this app.' : 'Could not load this app.')), [api, instanceId]);

    useEffect(() => { load(); }, [load]);

    const uninstall = hostId => api.del(`/api/apps/${instanceId}/hosts/${hostId}`)
        .then(() => { message.success('Uninstalled'); return load(); })
        .catch(err => message.error(err.status === 403 ? 'Only an admin of that entity can uninstall it' : 'Could not uninstall'));

    // The node's own install: the nav bar lists it for everyone, so refresh that too.
    const uninstallFromNode = () => api.del(`/api/apps/${instanceId}/node`)
        .then(() => { message.success('Uninstalled from the node'); refreshNodeApps(); return load(); })
        .catch(err => message.error(err.status === 403 ? 'Only a superadmin can uninstall an app from the node' : 'Could not uninstall'));

    if (error) return <div className="app-page"><Alert type="warning" message={error} showIcon /></div>;
    if (!instance) return <div className="app-page"><Spin /></div>;

    const View = VIEWS[instance.kind];
    return (
        <div className="app-page">
            <Space align="center" style={{ marginBottom: 4 }}>
                <Title level={3} style={{ margin: 0 }}>{instance.name}</Title>
                <Tag>{instance.kind}</Tag>
            </Space>
            <div className="app-page-hosts">
                {!instance.node_wide && instance.host_ids.length === 0 ? 'Not installed anywhere.' : <>Installed in{' '}
                    {instance.node_wide && (
                        <span>
                            <Text strong>this node</Text> (everyone signed in here)
                            {' '}
                            <Popconfirm title="Uninstall from the node?" onConfirm={uninstallFromNode} okText="Uninstall">
                                <Button type="link" size="small" danger style={{ padding: 0 }}>uninstall</Button>
                            </Popconfirm>
                        </span>
                    )}
                    {instance.host_ids.map((id, i) => (
                        <span key={id}>
                            {(i > 0 || instance.node_wide) && ', '}
                            <Link to={`/entity/${id}`}>{hosts[id]?.name || `#${id}`}</Link>
                            {' '}
                            <Popconfirm title="Uninstall from here?" onConfirm={() => uninstall(id)} okText="Uninstall">
                                <Button type="link" size="small" danger style={{ padding: 0 }}>uninstall</Button>
                            </Popconfirm>
                        </span>
                    ))}
                </>}
            </div>
            {instance.pending_version && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message={`Version ${instance.pending_version} asks for more: ${(instance.pending_scopes || []).filter(s => !instance.approved_scopes.includes(s)).join(', ')}. An admin approves it with “vellby apps approve ${instance.id}”.`} />
            )}
            {View ? <View instance={instance} /> : (
                <Text type="secondary">This app has no view in the web app yet. Its verbs and views are open through the API and MCP.</Text>
            )}
        </div>
    );
}

export default AppPage;
